import { Tooltip } from 'antd';
import { IConflictSearchInformativeIconProps } from '../interface/conflictSearch';
import {
  UserOctagonGreen,
  UserOctagonGrey,
  UserOctagonRed,
  WarningCircle,
} from '../components/icons';
import {
  DEFAULT_CLIENT_ROLE_IDENTIFIER,
  matterPartyRoleTranslationMapping,
  MatterPartyRoleType,
  OPPONENT_ROLE_ID,
  PAYER_ROLE_ID,
} from './conflictSearch';
import { TFunction } from 'i18next';
import { ICustomerType } from './customer';
import { IMatterParties, IRole } from '../interface/matterParties';
import { Button } from '../components/Ant';

const IconComponent = (Icon: React.ElementType, message: string, className: string) => {
  return (
    <Tooltip title={message} placement="top" arrow={false}>
      <Button
        aria-label={message}
        className={className}
        icon={<Icon />}
        onClick={(e) => {
          e.stopPropagation();
        }}
      />
    </Tooltip>
  );
};
export const getConflictSearchInformativeIcon = ({
  isBlackListed,
  hasActiveConflictChecks,
  hasMatterRequests,
  hasCoPartyRole,
  hasOppositionRole,
  t,
}: IConflictSearchInformativeIconProps) => {
  const icons = [];

  if (isBlackListed || hasActiveConflictChecks || hasMatterRequests) {
    icons.push(
      IconComponent(
        WarningCircle,
        t('conflict:customerOrPrivatePersonHasActiveMatterRequestConflictCheckOrIsBlackListed'),
        'important-note-btn',
      ),
    );
  }

  if (hasOppositionRole) {
    icons.push(
      IconComponent(
        UserOctagonRed,
        t('conflict:companyOrPrivatePersonIsOpponent'),
        'important-note-btn',
      ),
    );
    return icons.reverse();
  }

  if (!hasOppositionRole && hasCoPartyRole) {
    icons.push(
      IconComponent(
        UserOctagonGreen,
        t('conflict:companyOrPrivatePersonIsPayerClientOrCoPartyAndNotOpponent'),
        'important-note-btn',
      ),
    );
    return icons.reverse();
  }

  if (!hasOppositionRole && !hasCoPartyRole) {
    icons.push(
      IconComponent(
        UserOctagonGrey,
        t('conflict:companyOrPrivatePersonHasNoRoleAsOpponentPayerClientOrCoparty'),
        'important-note-btn',
      ),
    );
    return icons.reverse();
  }

  return icons;
};
export const displayRoles = (
  hasOtherRole: boolean,
  hasOppositionRole: boolean,
  hasCoPartyRole: boolean,
  t: TFunction,
) => {
  const roles: string[] = [];

  if (hasCoPartyRole) {
    roles.push(t(matterPartyRoleTranslationMapping[MatterPartyRoleType.COPARTY]));
  }
  if (hasOppositionRole) {
    roles.push(t(matterPartyRoleTranslationMapping[MatterPartyRoleType.OPPOSITION]));
  }
  if (hasOtherRole) {
    roles.push(t(matterPartyRoleTranslationMapping[MatterPartyRoleType.OTHER]));
  }

  return !roles.length ? '-' : roles.join(', ');
};
export const displayType = (type: string, t: TFunction) => {
  switch (type) {
    case ICustomerType.COMPANY:
      return t('customer:customerType.company');
    case ICustomerType.PRIVATE_PERSON:
      return t('customer:customerType.privatePerson');
    default:
      return '-';
  }
};
export const getConflictSearchMatterInformativeIcon = ({
  matterParty,
  t,
}: {
  matterParty: IMatterParties;
  t: TFunction;
}) => {
  const isOpponentRole = matterParty.roles?.some(
    (role) => role.matterPartyRoleId === OPPONENT_ROLE_ID,
  );
  const isPayerClientOrCoPartyRole = matterParty.roles?.some(
    (role) =>
      role.matterPartyRoleId === PAYER_ROLE_ID ||
      role.matterPartyRoleId === DEFAULT_CLIENT_ROLE_IDENTIFIER ||
      role.matterPartyRole.type === MatterPartyRoleType.COPARTY,
  );

  if (isOpponentRole) {
    return IconComponent(
      UserOctagonRed,
      t('conflict:companyOrPrivatePersonIsOpponentInMatter'),
      'important-note-btn',
    );
  }

  if (isPayerClientOrCoPartyRole && !isOpponentRole) {
    return IconComponent(
      UserOctagonGreen,
      t('conflict:companyOrPrivatePersonIsPayerClientOrCoPartyAndNotOpponentInMatter'),
      'important-note-btn',
    );
  }

  if (!isPayerClientOrCoPartyRole || !isOpponentRole) {
    return IconComponent(
      UserOctagonGrey,
      t('conflict:companyOrPrivatePersonIsNotPayerClientCoPartyOpponentInMatter'),
      'important-note-btn',
    );
  }
  return null;
};
export const getTranslatedRoles = (rolesArray: IRole[], languageCode: string): string => {
  return rolesArray
    .map(
      (roleObj) =>
        roleObj.matterPartyRole?.translations?.find((t) => t.language === languageCode)
          ?.translation,
    )
    .filter(Boolean) // removes undefined values
    .join(', ');
};
